import { familyImportRowSchema, familyImportRowToInput, type FamilyInput } from '../schemas/family';
import type { ImportError, ImportSummary } from './errors';
import { parseCsv } from './parseCsv';

// Expected headers (after normalization). The actual CSV may have any case
// or punctuation; normalizeHeader collapses to snake_case.
export const FAMILY_REQUIRED_HEADERS = [
  'guardian_name',
  'email',
  'phone',
  'address_line1',
  'city',
  'region',
  'postal_code',
] as const;

export const FAMILY_OPTIONAL_HEADERS = ['address_line2', 'country'] as const;

export interface FamilyImportResult {
  summary: ImportSummary;
  // Each valid row carries its original 1-indexed CSV row number so that
  // downstream reconciliation can attribute errors to the user's source file.
  validRows: { rowNum: number; data: FamilyInput }[];
}

export function validateFamilyCsv(csvText: string): FamilyImportResult {
  const { headers, rows } = parseCsv(csvText);
  const errors: ImportError[] = [];
  const warnings: ImportError[] = [];
  const validRows: { rowNum: number; data: FamilyInput }[] = [];

  // 1. Header validation.
  const headerSet = new Set(headers);
  for (const h of FAMILY_REQUIRED_HEADERS) {
    if (!headerSet.has(h)) {
      errors.push({
        row: 0,
        column: h,
        kind: 'header_missing',
        message: `Required column "${h}" is missing from the file.`,
      });
    }
  }
  for (const h of headers) {
    if (
      !FAMILY_REQUIRED_HEADERS.includes(h as never) &&
      !FAMILY_OPTIONAL_HEADERS.includes(h as never)
    ) {
      warnings.push({
        row: 0,
        column: h,
        kind: 'header_unknown',
        message: `Column "${h}" is not recognized and will be ignored.`,
      });
    }
  }

  if (errors.length > 0) {
    return {
      summary: {
        totalRows: rows.length,
        validRows: 0,
        invalidRows: rows.length,
        errors,
        warnings,
      },
      validRows: [],
    };
  }

  // 2. Per-row validation.
  const emailSeen = new Map<string, number>(); // lower → row#

  rows.forEach((row, idx) => {
    const rowNum = idx + 2; // 1-indexed + header row = +2
    const parsed = familyImportRowSchema.safeParse(row);

    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const column = issue.path.join('.') || '(row)';
        errors.push({
          row: rowNum,
          column,
          kind: 'row_invalid',
          message: issue.message,
          value: typeof row[column] === 'string' ? row[column] : undefined,
        });
      }
      return;
    }

    // 3. Duplicate-in-file detection.
    const emailLower = parsed.data.email.toLowerCase();
    const prior = emailSeen.get(emailLower);
    if (prior !== undefined) {
      errors.push({
        row: rowNum,
        column: 'email',
        kind: 'duplicate_in_file',
        message: `Email already appears in this file at row ${prior}.`,
        value: parsed.data.email,
      });
      return;
    }
    emailSeen.set(emailLower, rowNum);

    validRows.push({ rowNum, data: familyImportRowToInput(parsed.data) });
  });

  return {
    summary: {
      totalRows: rows.length,
      validRows: validRows.length,
      invalidRows: rows.length - validRows.length,
      errors,
      warnings,
    },
    validRows,
  };
}

// Cross-check against existing family emails in the database. Called by the
// Edge Function commit-time after validateFamilyCsv passes. Preserves the
// original CSV row number so error messages point to the correct source row.
export function reconcileFamilyImport(
  result: FamilyImportResult,
  existingEmails: Set<string>,
): FamilyImportResult {
  const errors = [...result.summary.errors];
  const filtered: { rowNum: number; data: FamilyInput }[] = [];

  for (const item of result.validRows) {
    if (existingEmails.has(item.data.primary_guardian_email.toLowerCase())) {
      errors.push({
        row: item.rowNum,
        column: 'email',
        kind: 'duplicate_with_db',
        message: 'A family with this email already exists.',
        value: item.data.primary_guardian_email,
      });
    } else {
      filtered.push(item);
    }
  }

  return {
    summary: {
      ...result.summary,
      validRows: filtered.length,
      invalidRows: result.summary.totalRows - filtered.length,
      errors,
    },
    validRows: filtered,
  };
}
